import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AnalyticsService, PolicyService } from '@campfire-nx/api';
import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';
import { DisplayDatePipe } from '@open-portal-app/core/pipes/display-date.pipe';
import { UserDataService } from '@open-portal-app/core/services/user-data.service';

import {
  AUTO_RENEWAL_FLOW_PAGE_ONE,
  AUTO_RENEWAL_FLOW_SUCCESS_PAGE,
  AUTO_RENEWAL_ERROR_PAGE,
  AUTO_RENEW_FINAL_PAGES,
} from '../constants/auto-renewal-flow-v001';

import {RENEWAL_ACTIVE_STATES, RENEWAL_INACTIVE_STATES,RENEWAL_ALLOWED_UPDATE_TO} from '../constants/auto-renewal.states'

@Injectable(
  {providedIn: 'root'}
)
export class AutoRenewalManagerService {
  steps!: any;
  currentStepIndex = 0;
  isAuthenticated!: boolean;
  eventCategory!: string;
  private policySlug!: string;
  private policy!: any;
  private activeStates = RENEWAL_ACTIVE_STATES;
  private inactiveStates = RENEWAL_INACTIVE_STATES;
  private updateToFlag: typeof RENEWAL_ALLOWED_UPDATE_TO[number] = null;

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private userDataService: UserDataService,
    private policyService: PolicyService,
    private policyDetailService: PolicyDetailsService,
    private displayDatePipe: DisplayDatePipe
  ) { }

  /**
   * Replaces placeholders in a template structure with values from a data object
   * @param template The template structure containing placeholders
   * @param data The object containing values to replace placeholders
   * @returns A new structure with all placeholders replaced by actual values
  */

  private replacePlaceholder<T>(template: T, data: Record<string, any>): T {
    if (typeof template === 'string') {
      // Replace placeholders in strings
      return template.replace(/\{(\w+)\}/g, (match, placeholder) => {
        return data[placeholder] !== undefined ? data[placeholder] : match;
      }) as unknown as T;
    } else if (Array.isArray(template)) {
        // Process each array element
        return template.map(item => this.replacePlaceholder(item, data)) as unknown as T;
    } else if (typeof template === 'object' && template !== null) {
        // Process object properties recursively
        const result: Record<string, any> = {};
        for (const [key, value] of Object.entries(template)) {
          result[key] = this.replacePlaceholder(value, data);
        }
      return result as T;
    }
    // Return non-string, non-object, non-array values as-is
    return template;
  }

  private getFlowPage(index: number): any {

    const replacements: Record<string, { value: string }> = {
      policyEndDate: {
        value: this.displayDatePipe.transform(this.policy.end_date, 'dd MMM yyyy')
      },
      productClass: {
        value: this.policy.product.base_product.class
      }
    }
    let replacedObject = this.steps[index];
    Object.entries(replacements).forEach(([key, {value}]) => {
      replacedObject = this.replacePlaceholder(
        replacedObject,
        { [key]: value },
      );
      return replacedObject
    });

    return replacedObject;
  }

  setAutoRenewalFlow() {
    const renewalFlag: string = this.policy.renewal_flag;
    // this is the default flow
    this.steps = [AUTO_RENEWAL_FLOW_PAGE_ONE, AUTO_RENEWAL_FLOW_SUCCESS_PAGE];
    // update flow_data with current renewal_flag value

    this.steps.forEach((obj: any) => {
      obj.data.renewal_flag = this.policy.renewal_flag;
    });

    if (this.activeStates.includes(renewalFlag)) {
      // DisableFlow
      this.eventCategory = 'RenewalFlagDisableFlow';
      this.updateToFlag = 'CD';
    } else if (this.inactiveStates.includes(renewalFlag)) {
      // EnableFlow
      this.eventCategory = 'RenewalFlagEnableFlow';
      this.updateToFlag = 'CE';
    } else {
      // Unsupported
      this.eventCategory = 'RenewalFlagUnsupportedFlow';
      this.steps = [AUTO_RENEWAL_ERROR_PAGE];
    }
  }

  getFirstStep(policySlug: string): Promise<any> {
    this.currentStepIndex = 0;
    this.policySlug = policySlug;
    return new Promise((resolve) => {
      this.policy = this.policyDetailService.policy;
      this.setAutoRenewalFlow();
      this.steps[0].analytics = {
        ...this.steps[0].analytics,
        policy_slug: this.policy.uuid
      };
      this.analyticsService.trackEvent('Initialise', this.eventCategory, 'start-autorenewal-flow', this.steps[0].analytics);
      resolve(this.getFlowPage(0));
    })
  }

  next(payload: any) {
    const isConfirmed: boolean = payload.confirmationOn || payload.confirmationOff || false;

    return new Promise((resolve) => {
      if (this.currentStepIndex === this.steps.length - 2) {
        // We are in the submission page
        if (this.updateToFlag && isConfirmed) {
          this.policyService.updateRenewalFlag(this.policySlug, this.updateToFlag)
            .then((response: any) => {
              if (response.status === 200) {
                this.userDataService.getLatestUserData();
                this.currentStepIndex += 1;
                resolve(this.getFlowPage(this.currentStepIndex));
              } else {
                resolve(AUTO_RENEWAL_ERROR_PAGE);
              }
            }).catch((error: any) => {
            resolve(AUTO_RENEWAL_ERROR_PAGE);
          })
        } else {
          resolve('');
          this.route_to_dashboard()
        }
      } else if (AUTO_RENEW_FINAL_PAGES.includes(this.steps[this.currentStepIndex].flow_slug)) {
        // this is the final page
        resolve('');
        this.route_to_dashboard();
      } else {
        // otherwise go to the next page
        this.currentStepIndex += 1;
        resolve(this.getFlowPage(this.currentStepIndex));
      }
    });
  }

  route_to_dashboard(): void {
    this.currentStepIndex = 0;
    this.router.navigate(['/dashboard']);
  }

  back() {
    return new Promise((resolve) => {
      if (this.currentStepIndex === 0) {
        this.router.navigate([`/policy/${this.policy.product.base_product.class}/${this.policySlug}`]);
      } else {
        this.currentStepIndex -= 1;
        resolve(this.getFlowPage(this.currentStepIndex))
    }
  })
  }
}
